import React from 'react';

// =============================================================================
// InvoicingStatsPanel
// =============================================================================
// The Accounting workspace's top grid (legacy dash_CSR_InvStats / dash_invstats_row):
// six accounting metrics across Daily / Weekly / Monthly, each split into Previous /
// Current / Goal. Purely presentational read-out of pre-computed values from
// /lc-dashboards/invoicing-stats — nothing is clickable and it ignores the page's
// global filters. Each Previous/Current value carries an up/down arrow colored by
// whether it's beating its goal (for Avg-Days metrics, lower is better, so the
// good/bad coloring is inverted).

export interface InvCell { previous: number; current: number; goal: number }
export interface InvoicingStatsData {
  stats: { [type: string]: { daily: InvCell; weekly: InvCell; monthly: InvCell } };
}

export interface InvoicingStatsPanelProps {
  data: InvoicingStatsData | null;
  loading: boolean;
}

type Fmt = 'money' | 'int' | 'dec2';
const ROWS: { type: string; label: string; fmt: Fmt; higherIsBetter: boolean }[] = [
  { type: 'revenue', label: 'Invoiced Revenue', fmt: 'money', higherIsBetter: true },
  { type: 'orders', label: 'Invoiced Orders', fmt: 'int', higherIsBetter: true },
  { type: 'avgdaystobill', label: 'Avg Days to Bill', fmt: 'dec2', higherIsBetter: false },
  { type: 'sentrevenue', label: 'Sent to Acct Revenue', fmt: 'money', higherIsBetter: true },
  { type: 'sentorders', label: 'Sent to Acct Orders', fmt: 'int', higherIsBetter: true },
  { type: 'avgdaystoacct', label: 'Avg Days to Acct', fmt: 'dec2', higherIsBetter: false },
];
const PERIODS: ('daily' | 'weekly' | 'monthly')[] = ['daily', 'weekly', 'monthly'];

const fmtValue = (fmt: Fmt, v: number): string => {
  if (fmt === 'money') return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (fmt === 'int') return Math.round(v).toLocaleString('en-US');
  return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const HEADER = 'px-2 py-1 font-semibold text-center border-l border-slate-500 whitespace-nowrap';
const LABEL_CELL = 'px-2 py-1 whitespace-nowrap border-t border-gray-100 dark:border-gray-700/50';
const VAL_CELL = 'px-2 py-1 text-right tabular-nums whitespace-nowrap border-l border-t border-gray-100 dark:border-gray-700/50';

const EMPTY: InvCell = { previous: 0, current: 0, goal: 0 };

// Arrow for a Previous/Current value vs its goal. Direction follows value-vs-goal;
// color follows whether that's good for the metric (Avg-Days metrics invert).
const Indicator: React.FC<{ value: number; goal: number; higherIsBetter: boolean }> = ({ value, goal, higherIsBetter }) => {
  const up = value >= goal;
  const good = higherIsBetter ? up : !up;
  return (
    <span className={`ml-1 ${good ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} aria-hidden>
      {up ? '▲' : '▼'}
    </span>
  );
};

const InvoicingStatsPanel: React.FC<InvoicingStatsPanelProps> = ({ data, loading }) => {
  const stats = data?.stats;

  return (
    // w-full so the table fills the tile width (header + columns stretch to the panel edges);
    // natural height keeps the tile's double-click fit-to-content working.
    <div className="w-full bg-panel text-sm">
      <table className="border-collapse w-full">
        <thead>
          <tr className="bg-slate-700 text-white">
            <th rowSpan={2} className="px-2 py-1 font-semibold text-center align-middle min-w-[150px]">Invoicing Statistics</th>
            <th colSpan={3} className={HEADER}>Daily</th>
            <th colSpan={3} className={HEADER}>Weekly</th>
            <th colSpan={3} className={HEADER}>Monthly</th>
          </tr>
          <tr className="bg-slate-700 text-white">
            {PERIODS.map((p) => (
              <React.Fragment key={p}>
                <th className={`${HEADER} min-w-[96px]`}>Previous</th>
                <th className={`${HEADER} min-w-[96px]`}>Current</th>
                <th className={`${HEADER} min-w-[96px]`}>Goal</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody className="text-body">
          {ROWS.map((row) => (
            <tr key={row.type} className="even:bg-gray-50 dark:even:bg-gray-700/30">
              <td className={`${LABEL_CELL} font-medium`}>{row.label}</td>
              {PERIODS.map((p) => {
                const c = stats?.[row.type]?.[p] ?? EMPTY;
                return (
                  <React.Fragment key={p}>
                    <td className={VAL_CELL}>
                      {fmtValue(row.fmt, c.previous)}
                      <Indicator value={c.previous} goal={c.goal} higherIsBetter={row.higherIsBetter} />
                    </td>
                    <td className={VAL_CELL}>
                      {fmtValue(row.fmt, c.current)}
                      <Indicator value={c.current} goal={c.goal} higherIsBetter={row.higherIsBetter} />
                    </td>
                    <td className={`${VAL_CELL} text-gray-400 dark:text-gray-500`}>
                      {fmtValue(row.fmt, c.goal)}
                    </td>
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
          {!stats && loading && (
            <tr><td colSpan={10} className="px-2 py-4 text-center text-gray-400">Loading…</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default InvoicingStatsPanel;
